import { Map, MapLayerEventType, Popup } from "maplibre-gl"
import AvoidanceItemsParser, {
  AvoidanceItems,
} from "@/lib/avoidanceItemsParser"
import AdHocCommands from "@/api/adHocCommands"
import i18n from "@/lib/i18n/i18n"

export class AvoidanceAreasManager {
  private map: Map | null = null
  private readonly sourceId = "avoidance-areas-source"
  private readonly layerFillId = "avoidance-areas-layer-fill"
  private readonly layerBorderId = "avoidance-areas-layer-border"
  private popup: Popup | null = null
  private onGetAvoidanceItems: () => Promise<AvoidanceItems | undefined>
  private boundEventHandlers: {
    event: string
    layerId: string
    handler: (e: any) => void
  }[] = []

  constructor(
    map: Map,
    onGetAvoidanceItems: () => Promise<AvoidanceItems | undefined>,
  ) {
    this.map = map
    this.onGetAvoidanceItems = onGetAvoidanceItems
    this.popup = new Popup({
      closeButton: true,
      closeOnClick: true,
    })
  }

  /**
   * Fetches avoidance areas for a specific driver and adds them to the map
   * @param driverJid The JID of the driver to fetch avoidance areas for
   * @returns Promise that resolves when avoidance areas are added to the map
   */
  async getAvoidanceItems(): Promise<void> {
    try {
      if (!this.map) {
        console.warn("Map not initialized when trying to fetch avoidance items")
        return
      }
      const avoidanceItems = await this.onGetAvoidanceItems?.()
      if (avoidanceItems) {
        this.addAvoidanceAreas(avoidanceItems)
      }
    } catch (error) {
      console.error("Failed to fetch avoidance items:", error)
    }
  }

  public addAvoidanceAreas(geojson: AvoidanceItems): void {
    if (!this.map) return

    // Clear any existing avoidance areas first
    this.clear()

    this.map.addSource(this.sourceId, {
      type: "geojson",
      data: geojson,
    })

    this.map.addLayer({
      id: this.layerFillId,
      type: "fill",
      source: this.sourceId,
      layout: {},
      paint: {
        "fill-color": "#880034",
        "fill-opacity": 0.2,
      },
    })

    this.map.addLayer({
      id: this.layerBorderId,
      type: "line",
      source: this.sourceId,
      paint: {
        "line-color": "#880034",
        "line-width": 2,
      },
    })

    // Add event handlers
    const clickHandler = (e: any) => {
      if (!e.features || e.features.length === 0 || !this.map) return

      const feature = e.features[0]
      const properties = feature.properties

      if (!properties) return

      // Create HTML content for popup
      const html = `
        <div class="p-2 pr-6">
          <h3 class="font-medium text-base">${properties.name || i18n.t("maps.avoidanceAreasManager.unnamedArea")}</h3>
          ${properties.description ? `<p class="text-sm mt-1">${properties.description}</p>` : ""}
        </div>
      `

      // Set popup content and location
      this.popup?.setLngLat(e.lngLat).setHTML(html).addTo(this.map)
    }

    const mouseEnterHandler = () => {
      if (this.map) this.map.getCanvas().style.cursor = "pointer"
    }

    const mouseLeaveHandler = () => {
      if (this.map) this.map.getCanvas().style.cursor = ""
    }

    // Add event handlers
    this.map.on("click", this.layerFillId, clickHandler)
    this.map.on("mouseenter", this.layerFillId, mouseEnterHandler)
    this.map.on("mouseleave", this.layerFillId, mouseLeaveHandler)

    // Store the handlers for later removal
    this.boundEventHandlers = [
      { event: "click", layerId: this.layerFillId, handler: clickHandler },
      {
        event: "mouseenter",
        layerId: this.layerFillId,
        handler: mouseEnterHandler,
      },
      {
        event: "mouseleave",
        layerId: this.layerFillId,
        handler: mouseLeaveHandler,
      },
    ]
  }

  public clear(): void {
    if (!this.map) return

    // Remove event handlers
    this.boundEventHandlers.forEach(({ event, layerId, handler }) => {
      this.map?.off(event as keyof MapLayerEventType, layerId, handler)
    })
    this.boundEventHandlers = []

    // Remove popup if it exists
    if (this.popup?.isOpen()) {
      this.popup.remove()
    }

    // Remove layers if they exist
    if (this.map.getLayer(this.layerBorderId)) {
      this.map.removeLayer(this.layerBorderId)
    }

    if (this.map.getLayer(this.layerFillId)) {
      this.map.removeLayer(this.layerFillId)
    }

    // Remove source if it exists
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId)
    }
  }

  public destroy(): void {
    this.clear()
    this.map = null
    this.popup = null
  }
}
